import { forwardRef, useId, type OptionHTMLAttributes, type ReactNode, type SelectHTMLAttributes } from 'react';
import { cn } from '../../utils/cn';
import {
    FIELD_BASE_CLASSES,
    FIELD_ERROR_BORDER_CLASSES,
    FIELD_ERROR_TEXT_CLASSES,
    FIELD_FOCUS_CLASSES,
    FIELD_HINT_CLASSES,
    FIELD_LABEL_CLASSES,
} from './fieldStyles';

export interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
    label?: string;
    hint?: string;
    error?: string;
    children: ReactNode;
    /** Layout only (width, margin) — applied to the wrapper, never the control. */
    className?: string;
}

/**
 * Wraps a native `<select>` — no custom listbox. The app has no such
 * dependency and native is better on mobile. `<option>` gets an explicit
 * background so it stays readable in dark mode on Windows, where the OS
 * otherwise renders the popup list in its own light theme.
 */
export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
    { label, hint, error, children, className, id, ...rest },
    ref,
) {
    const generatedId = useId();
    const selectId = id ?? generatedId;
    const hintId = hint ? `${selectId}-hint` : undefined;
    const errorId = error ? `${selectId}-error` : undefined;
    const describedBy = [errorId, hintId].filter(Boolean).join(' ') || undefined;

    return (
        <div className={className}>
            {label && (
                <label htmlFor={selectId} className={FIELD_LABEL_CLASSES}>
                    {label}
                </label>
            )}
            <select
                ref={ref}
                id={selectId}
                aria-invalid={error ? true : undefined}
                aria-describedby={describedBy}
                className={cn(
                    FIELD_BASE_CLASSES,
                    FIELD_FOCUS_CLASSES,
                    error && FIELD_ERROR_BORDER_CLASSES,
                    '[color-scheme:dark]',
                )}
                {...rest}
            >
                {children}
            </select>
            {error ? (
                <p id={errorId} className={FIELD_ERROR_TEXT_CLASSES}>
                    {error}
                </p>
            ) : hint ? (
                <p id={hintId} className={FIELD_HINT_CLASSES}>
                    {hint}
                </p>
            ) : null}
        </div>
    );
});

export type SelectOptionProps = OptionHTMLAttributes<HTMLOptionElement>;

/**
 * An `<option>` with an explicit background/text pair. Some Windows browsers
 * render the native popup list in the OS light theme regardless of the
 * page's own colours, so a plain `<option>` inside a dark `Select` can end
 * up unreadable — this fixes the pair the same way `Select` fixes its own.
 */
export function SelectOption({ className, ...rest }: SelectOptionProps) {
    return <option className={cn('bg-space-950 text-content-primary', className)} {...rest} />;
}

export default Select;
